import { useMemo, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import {
  CARRIER_VALUE_TO_IMO,
  carrierPlanLabel,
  formatSubmissionStatusForAgentProfile,
  isSubmissionAutofillComplete,
} from "@aam/shared";
import { AgentDetailSkeleton } from "../skeletons/AgentDetailSkeleton";
import { AgentDetailHeader } from "./AgentDetailHeader";
import { ContractsTable } from "./ContractsTable";
import { CarrierChangeRequestsPanel } from "../CarrierChangeRequestsPanel";
import { ProfileSectionsView, providerIntakeFieldIds } from "./profileHelpers";
import { API_BASE } from "./types";
import { useAgentDetail } from "./useAgentDetail";

/** Shown in the agent card, so the intake section does not repeat them. */
const IDENTITY_FIELD_IDS = ["firstName", "lastName", "email", "phone", "npn"];

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border-subtle py-2.5 last:border-0 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-content-muted sm:w-44 sm:shrink-0">
        {label}
      </dt>
      <dd className="min-w-0 break-words text-sm text-content-primary">{value}</dd>
    </div>
  );
}

export function ProviderAgentDetail() {
  const { agentId } = useParams<{ agentId: string }>();
  const { token, loading, error, setError, user, contracts, reload } = useAgentDetail(agentId);
  const [saving, setSaving] = useState<string | null>(null);
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const [revealingIds, setRevealingIds] = useState<Set<string>>(new Set());
  const [revealedValues, setRevealedValues] = useState<Map<string, string>>(new Map());

  const profileSubmission = user?.profileSubmission ?? user?.latestSubmission;
  const scopedImo = user?.capabilities.visibleImos[0];

  const scopedCarriers = useMemo(() => {
    const fromContracts = [...new Set(contracts.map((contract) => contract.carrier))];
    if (fromContracts.length > 0) {
      return fromContracts.sort((left, right) => left.localeCompare(right));
    }
    if (!scopedImo || !profileSubmission) return [];
    return profileSubmission.carriers
      .filter((carrier) => CARRIER_VALUE_TO_IMO[carrier] === scopedImo)
      .sort((left, right) => left.localeCompare(right));
  }, [contracts, profileSubmission, scopedImo]);

  const visibleFieldIds = useMemo(() => {
    if (!profileSubmission || !scopedImo) return undefined;
    const ids = providerIntakeFieldIds(scopedImo, profileSubmission.carriers);
    if (scopedCarriers.length > 0) {
      ids.delete("selectedCarriers");
    }
    // The agent card above already carries identity, so the intake section
    // shows only what the carrier actually asked for.
    for (const identityField of IDENTITY_FIELD_IDS) ids.delete(identityField);
    return ids;
  }, [profileSubmission, scopedImo, scopedCarriers.length]);

  if (loading) return <AgentDetailSkeleton isProviderViewer />;
  if (!user) {
    return <div className="rounded-xl border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text">Agent not found.</div>;
  }

  const capabilities = user.capabilities;
  const formData = profileSubmission?.formData ?? {};
  const agentPhone = typeof formData.phone === "string" && formData.phone.trim()
    ? formData.phone
    : null;

  async function updateStatus(contractId: string, nextStatus: string, comment: string) {
    if (!token || !agentId) return;
    setSaving(contractId);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/provider-contracts/${contractId}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status: nextStatus, notes: comment.trim() || undefined }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to update contract");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update contract");
    } finally {
      setSaving(null);
    }
  }

  async function revealField(fieldId: string) {
    if (revealedIds.has(fieldId)) {
      setRevealedIds((current) => {
        const next = new Set(current);
        next.delete(fieldId);
        return next;
      });
      return;
    }
    if (revealedValues.has(fieldId)) {
      setRevealedIds((current) => new Set(current).add(fieldId));
      return;
    }
    if (!token || !agentId || !profileSubmission) return;
    setRevealingIds((current) => new Set(current).add(fieldId));
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/agents/${agentId}/submissions/${profileSubmission.id}/reveal-field`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ fieldId }),
        }
      );
      const payload = await res.json();
      if (!res.ok || !payload.success) {
        throw new Error(payload.error || "Unable to reveal field");
      }
      setRevealedValues((current) => new Map(current).set(fieldId, String(payload.data.value)));
      setRevealedIds((current) => new Set(current).add(fieldId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to reveal field.");
    } finally {
      setRevealingIds((current) => {
        const next = new Set(current);
        next.delete(fieldId);
        return next;
      });
    }
  }

  async function downloadUploadedFile(fileId: string, fileName: string) {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/uploads/${encodeURIComponent(fileId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to download file");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName || "upload";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to download file");
    }
  }

  return (
    <section className="space-y-4">
      <AgentDetailHeader
        displayName={user.displayName || user.email}
        email={user.email}
        shell="provider"
        upline={user.upline}
      />

      {error && (
        <div className="rounded-xl border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text">
          {error}
        </div>
      )}

      <section className="rounded-xl border border-border-default bg-surface-raised p-5">
        <h2 className="mb-3 font-display text-base font-semibold text-content-primary">Agent</h2>
        <dl className="grid gap-x-8 md:grid-cols-2">
          <DetailRow label="Name" value={user.displayName || "—"} />
          <DetailRow label="Email" value={user.email} />
          <DetailRow label="Phone" value={agentPhone ?? "—"} />
          <DetailRow label="NPN" value={user.npn || "—"} />
          <DetailRow label="Organization" value={user.organization?.brandName ?? "—"} />
          <DetailRow
            label="Upline"
            value={
              user.upline ? (
                <>
                  {user.upline.displayName}
                  <span className="ml-2 text-xs text-content-muted">{user.upline.email}</span>
                </>
              ) : (
                "—"
              )
            }
          />
          <DetailRow
            label="Carriers"
            value={
              scopedCarriers.length > 0 ? (
                <span className="flex flex-wrap gap-1.5">
                  {scopedCarriers.map((carrier) => (
                    <span
                      key={carrier}
                      className="rounded-full border border-border-default bg-surface-overlay px-2.5 py-0.5 text-xs text-content-secondary"
                    >
                      {carrierPlanLabel(carrier)}
                    </span>
                  ))}
                </span>
              ) : (
                "—"
              )
            }
          />
        </dl>
      </section>

      {profileSubmission && capabilities.viewMode === "provider-intake" && (
        <section className="rounded-xl border border-accent/30 bg-accent/10 p-4 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-xs uppercase tracking-[0.25em] text-accent">Carrier intake</h2>
            {!isSubmissionAutofillComplete(profileSubmission.status) && (
              <span className="rounded-full bg-surface-base px-2.5 py-1 text-xs text-content-primary">
                {formatSubmissionStatusForAgentProfile(profileSubmission.status)}
              </span>
            )}
          </div>
          <ProfileSectionsView
            formData={profileSubmission.formData ?? {}}
            carriers={profileSubmission.carriers}
            visibleFieldIds={visibleFieldIds}
            scopedCarriers={scopedCarriers}
            onReveal={revealField}
            revealedIds={revealedIds}
            revealingIds={revealingIds}
            revealedValues={revealedValues}
            allowFileDownloads
            onDownloadFile={downloadUploadedFile}
          />
        </section>
      )}

      <ContractsTable
        contracts={contracts}
        capabilities={capabilities}
        onUpdateStatus={capabilities.canUpdateContractStatus ? updateStatus : undefined}
        saving={saving}
      />

      <CarrierChangeRequestsPanel
        agentId={user.id}
        user={user}
        contracts={contracts}
        canManage={capabilities.canUpdateContractStatus}
      />
    </section>
  );
}
